/**
 * Web Vitals — Real User Monitoring
 *
 * Reports Core Web Vitals (LCP, CLS, INP) and supplementary metrics (TTFB, FCP)
 * to both Sentry (performance spans) and PostHog (custom events).
 *
 * Call `initWebVitals()` once at app startup — it registers observers that
 * fire asynchronously as each metric finalizes.
 */

import { onLCP, onCLS, onINP, onTTFB, onFCP, type Metric } from 'web-vitals';
import * as Sentry from '@sentry/react';
import { capturePostHogEvent } from './posthog';
import { POSTHOG_EVENTS } from './posthog-events';

function reportMetric(metric: Metric): void {
  // ── PostHog ────────────────────────────────────────────────────────
  capturePostHogEvent(POSTHOG_EVENTS.web_vital_reported, {
    vital_name: metric.name,
    vital_value: metric.value,
    vital_rating: metric.rating,
    vital_delta: metric.delta,
    vital_id: metric.id,
    vital_navigation_type: metric.navigationType,
  });

  // ── Sentry ─────────────────────────────────────────────────────────
  // Attach as a measurement on the current active span (if tracing is enabled).
  const activeSpan = Sentry.getActiveSpan();
  if (activeSpan) {
    const rootSpan = Sentry.getRootSpan(activeSpan);
    if (rootSpan) {
      rootSpan.setAttribute(`web_vital.${metric.name}`, metric.value);
      rootSpan.setAttribute(`web_vital.${metric.name}.rating`, metric.rating);
    }
  }

  // Also set as Sentry measurement for dashboard aggregation.
  Sentry.setMeasurement(metric.name, metric.value, metric.name === 'CLS' ? '' : 'millisecond');
}

export function initWebVitals(): void {
  onLCP(reportMetric);
  onCLS(reportMetric);
  onINP(reportMetric);
  onTTFB(reportMetric);
  onFCP(reportMetric);
}
